import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { unstable_usePrompt as usePrompt } from 'react-router-dom'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import SearchButton from 'components/form/SearchButton'
import CourseTeeBoxes from 'components/form/course/CourseTeeBoxes'
import { selectCourse, clearTeeBoxMapping } from 'store/course/slice'
import { doCourseTeeBoxMapping } from 'store/course/thunks'
import DateTimePicker from '../DateTimePicker'
import { convertDateTimeToUTC, getCurrentUTCDateTime } from 'utils/formatDate'

interface Props {
  open: boolean
  close: () => void
  course: Course
  activeCourseVersion: CourseVersion
  previousCourseVersion: CourseVersion
}

export default function TeeBoxMappingModal({
  open,
  close,
  course,
  activeCourseVersion,
  previousCourseVersion,
}: Props): JSX.Element {
  const [mappingData, setMappingData] = useState<CourseTeeBoxMapping[]>([])
  const [teeBoxSelected, setTeeBoxSelected] = useState<boolean>(false)
  const { teeBoxMappingStatus } = useSelector(selectCourse)
  const dispatch = useDispatch()
  const intl = useIntl()
  const startTimeRef = useRef<HTMLInputElement | undefined>(undefined)
  const isCourseVersionEditAllowed = course.provider?.options?.courseVersionEditAllowed === true

  useEffect(() => {
    if (teeBoxMappingStatus === 'succeeded') {
      close()
      dispatch(clearTeeBoxMapping())
    }
  }, [teeBoxMappingStatus, close, dispatch])

  useEffect(() => {
    function confirmLeaving(event: any) {
      event.preventDefault()
      return (event.returnValue = leavePageConfirmationMessage())
    }
    window.addEventListener('beforeunload', confirmLeaving)

    return () => {
      window.removeEventListener('beforeunload', confirmLeaving)
    }
  })

  const leavePageConfirmationMessage = () => {
    return intl.formatMessage({ id: 'strings.leavePageConfirmation' })
  }

  usePrompt({ when: true, message: leavePageConfirmationMessage() })

  const syncData = (data: CourseTeeBoxMapping[]) => {
    const newData = data.filter((tb) => tb.oldTeeBoxId !== undefined)
    setMappingData(newData)
    setTeeBoxSelected(newData.length > 0)
  }

  const submitTeeBoxMapping = () => {
    let payload: CourseTeeBoxMappingPayload = {
      courseId: course.id,
      data: {
        teeBoxes: mappingData,
      },
    }
    if (isCourseVersionEditAllowed) {
      const startTime = startTimeRef.current?.value ? new Date(startTimeRef.current.value) : new Date()
      const startTimeString = convertDateTimeToUTC(startTime)

      payload = {
        ...payload,
        data: {
          ...payload.data,
          previousOfficialCourseVersionId: previousCourseVersion?.id,
          versionActiveStartTime: startTimeString,
        },
      }
    }
    dispatch(doCourseTeeBoxMapping(payload))
  }

  return (
    <>
      <StyledDialog open={open}>
        <DialogTitle id="form-dialog-title">{intl.formatMessage({ id: 'strings.mapCourseTeeBoxes' })}</DialogTitle>
        <DialogContent>
          <CourseTeeBoxes
            version={activeCourseVersion}
            secondaryVersion={previousCourseVersion}
            syncTeeBoxes={() => null}
            syncMappingData={syncData}
            isModifiable={true}
          />
        </DialogContent>
        <DialogActions>
          <CreateMappingButton
            status={teeBoxMappingStatus}
            text={intl.formatMessage({ id: 'strings.createTeeBoxMapping' })}
            onClick={submitTeeBoxMapping}
            disabled={!teeBoxSelected}
          />
          {isCourseVersionEditAllowed && (
            <StartTimePicker
              name="startTime"
              label={intl.formatMessage({ id: 'strings.startTime' })}
              inputRef={startTimeRef}
              initialValue={getCurrentUTCDateTime()}
              showSeconds
            />
          )}
        </DialogActions>
      </StyledDialog>
    </>
  )
}

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 1200px;
  }

  & .MuiTextField-root {
    margin: 20px 20px 20px 0;
  }

  & .MuiAlert-root {
    margin-bottom: 20px;
  }
  & .MuiDialogActions-root {
    justify-content: space-between;
    margin: 0 20px;
  }
`

const CreateMappingButton = styled(SearchButton)``

const StartTimePicker = styled(DateTimePicker)`
  margin-top: 0 !important;
`
